import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EventCard from "@/components/feature/EventCard";
import { useSEO } from "@/hooks/useSEO";
import { useHashScroll } from "@/hooks/useHashScroll";
import { categoryList, eventList, sortByHeat } from "@/utils/content";

export default function Events() {
  useHashScroll();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const queryParam = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(queryParam);

  const filtered = useMemo(() => {
    const key = queryParam.trim().toLowerCase();
    return sortByHeat().filter((event) => {
      const matchCategory =
        activeCategory === "all" || event.categorySlug === activeCategory;
      if (!matchCategory) return false;
      if (!key) return true;
      const haystack = [event.title, event.summary, ...event.tags]
        .join(" ")
        .toLowerCase();
      return haystack.includes(key);
    });
  }, [activeCategory, queryParam]);

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === "all") params.delete("category");
      else params.set("category", next.category);
    }
    if (next.q !== undefined) {
      if (!next.q) params.delete("q");
      else params.set("q", next.q);
      setKeyword(next.q);
    }
    setSearchParams(params, { replace: true });
  };

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "瓜田总览",
      description: "热搜吃瓜收录的全部事件，按热度排序，可按主题分类与关键词筛选。",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: filtered.length,
        itemListElement: filtered.map((event, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: event.title,
          url: `https://hot-melon-watch.example.com/event/${event.slug}`,
        })),
      },
    }),
    [filtered],
  );

  useSEO({
    title: "瓜田总览 | 全部热搜事件梳理与时间线复盘 - 热搜吃瓜",
    description:
      "浏览热搜吃瓜收录的全部事件，按热度排序，可按娱乐风波、综艺现场、影视动态等主题分类筛选，也能用关键词快速定位想看的事件。",
    keywords: "热搜吃瓜事件列表,热搜吃瓜事件梳理,热搜吃瓜时间线,瓜田总览",
    path: "/events",
    jsonLd,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <div className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs items={[{ label: "瓜田总览" }]} />
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              瓜田总览 · 全部事件梳理
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              共收录 {eventList.length} 个事件，每个都写明前因、经过、结果与完整时间线。
              先选分类或用关键词搜索，再按热度顺序往下读。
            </p>
          </div>
        </div>

        <section className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => updateParams({ category: "all" })}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                  activeCategory === "all"
                    ? "bg-primary-500 font-medium text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                全部
              </button>
              {categoryList.map((category) => (
                <button
                  key={category.slug}
                  type="button"
                  onClick={() => updateParams({ category: category.slug })}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    activeCategory === category.slug
                      ? "bg-primary-500 font-medium text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <i className={category.icon}></i>
                  {category.name}
                </button>
              ))}
            </div>

            <form
              className="relative w-full md:w-72"
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                updateParams({ q: keyword.trim() });
              }}
            >
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索事件标题或标签"
                aria-label="搜索事件标题或标签"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            </form>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-medium text-primary-700">{filtered.length}</span> 个事件
            </p>
            {(queryParam || activeCategory !== "all") && (
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams(new URLSearchParams(), { replace: true });
                }}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-700"
              >
                <i className="ri-refresh-line"></i>
                清除筛选
              </button>
            )}
          </div>

          {filtered.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((event, index) => (
                <EventCard key={event.id} event={event} index={index} />
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                没有找到匹配的事件
              </h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换个关键词试试，或者清除筛选条件查看全部事件。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams(new URLSearchParams(), { replace: true });
                }}
                className="mt-5 cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部事件
              </button>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}